/* eslint-disable react/prop-types */
import { PropertyCard } from '@/card/card-viewer/property-card/property-card';
import { useTransition } from 'react-spring';
import { PROPERTY_CARDS_PER_PAGE } from '../card-constants';
import {
  AnimatedGridWrapper,
  PageWrapper,
  PropertyCardGrid,
} from './card-viewer-styles';

/**
 * Assembles a "Page" of property cards.
 * AnimationWrapper
 * |
 * --PropertyCardGrid
 *         |
 *          -- PropertyCard[]
 * PropertyCards are taken by slicing appropriate portions of the propertyList
 * in groups specified by number of cards per page.
 * @param {*} props
 * @param {object} transition - styles provided by useTransition hook
 */
const CardPage = (props) => {
  const { currentPage, transition, previousPage, propertyList } = props;
  /**
   * If the user navigates to a "greater" page number ie. going from page 1 to 2 then the animation
   * should show the outgoing page sliding left. If the user navigates to a "lesser" page ie
   * page 2 - 1 then the animation should go to the right.
   *
   * The previous page is a ref to maintain a previous value without causing a re-render when the
   * value changes.
   *
   * When setCurrentPage is called, the currentPage variable in state will update and be different
   * than the previousPage value. We use this difference to determine whether the user is going to
   * a lesser or greater page.
   *
   * previousPage.current = item; is the line where we save the currentPage as the previous page.
   */
  previousPage.current = currentPage;

  return (
    <AnimatedGridWrapper style={{ ...transition }}>
      <PropertyCardGrid data-testid="property-card-grid">
        {propertyList
          .slice(
            currentPage * PROPERTY_CARDS_PER_PAGE - PROPERTY_CARDS_PER_PAGE,
            currentPage * PROPERTY_CARDS_PER_PAGE
          )
          .map((platformAssetInfo) => (
            <PropertyCard
              key={platformAssetInfo.deckardid}
              currentPage={currentPage}
              propertyData={platformAssetInfo}
            />
          ))}
      </PropertyCardGrid>
    </AnimatedGridWrapper>
  );
};

// Animation object passed to react spring. Animation direction based on current page and prev page
const getAnimation = (currentPage, previousPage) => ({
  from: {
    opacity: 0,
    transform:
      currentPage > previousPage.current
        ? 'translate3d(100%,0,0)'
        : 'translate3d(-100%,0,0)',
  },
  enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
  leave: {
    opacity: 0,
    transform:
      currentPage > previousPage.current
        ? 'translate3d(-50%,0,0)'
        : 'translate3d(50%,0,0)',
  },
});

/**
 * Each Page is wrapped by an animated div with a grid as a child and it's children
 * are property cards.
 *
 * @export CardViewerAnimatedPages
 * @param {*} props
 * @returns JSX.Element
 */
export function CardViewerAnimatedPages(props) {
  const { currentPage, previousPage, paginationContainer, requestContact } =
    props;

  /**
   * React spring hook for defining the animation behavior on enter and leave
   * useTransition(item, key, animation)
   * An animated TransitionGroup.
   * Feed it your items, keys (which can be null if items are atomic), and lifecycles.
   * Whenever items are added or removed, it will animate these changes.
   */
  const transitions = useTransition(
    currentPage,
    null,
    getAnimation(currentPage, previousPage)
  );

  return (
    <PageWrapper ref={paginationContainer} currentPage={currentPage}>
      {transitions.map((transition) => (
        <CardPage
          key={transition.key}
          currentPage={transition.item}
          transition={transition.props}
          previousPage={previousPage}
          propertyList={props.propertyList}
          requestContact={requestContact}
        />
      ))}
    </PageWrapper>
  );
}
